import React, { ComponentType, useEffect, useMemo, useState } from "react";
import {BookingType} from 'app/app.types';
import PaymentLinkInfo from "./index";
import { IProps, IBaseProps, IContainerProps, IServices, IHandlers, classPrefix } from './types';
import { first } from 'rxjs/operators';
/**
* Gets the interface types as array of strings so we don't need hand code them each time (and reduces chance for mistakes).
*/
import {keys} from 'ts-transformer-keys';
import {hot} from "react-hot-loader";
import ReactUtilsService from "app/services/reactUtils.service";
import { StandbyListService as LegacyStandbyListService } from 'app/services/standbyList/StandbyList.service';
import AdminService from "external-services/diary-admin-service/admin.service";
import CustomerValidationService from "app/services/customerValidation/customerValidation.service";
import CustomerNotificationService from "app/services/customerNotification/customerNotification.service";
import DateUtilsService from 'external-services/date-utils-service';
import CustomerLookupService from 'external-services/customer-lookup-service';
import { IBooking, ICustomer } from 'shared-types/SharedTypes';
import CustomerUpdateService from "external-services/customer-update-service/customerUpdate.service";
import PhoneNumberService from 'external-services/phone-number-service';
import PaymentValidationService from 'app/services/paymentValidation/paymentValidation.service';
import { bookingStatusType } from "shared-types/index";
import BookingUpdateService from 'external-services/booking-update-service';

const NS = 'PaymentLinkInfoContainer';

const propsKeys: string[] = keys<IProps>();
const basePropsKeys: string[] = keys<IBaseProps>();
const containerPropsKeys: string[] = keys<IContainerProps>();
const handlersKeys: string[] = keys<IHandlers>();
const servicesKeys: string[] = keys<IServices>();

// just exported for tests
export const ThemeWrap = function(all: IContainerProps & IServices) {
  /**
    * Sorts 'all' into IServices, IProps and IContainerProps
    */
  // const {}: IServices = ReactUtilsService.getObjectByKeys(all, servicesKeys) as IServices;
  const props: IProps = ReactUtilsService.getObjectByKeys(all, propsKeys) as IProps;

  const containerProps: IContainerProps = ReactUtilsService.getObjectByKeys(all, containerPropsKeys) as IContainerProps;
  const [newEmail, setNewEmail] = useState<string>(null);
  const [newMobilePhone, setNewMobilePhone] = useState<string>(props.mobilePhone);
  const [newBookedByMobilePhone, setNewBookedByMobilePhone] = useState<string>(props.booking.bookedBy?.phone);
  const [saveEmailToCustomer, setSaveEmailToCustomer] = useState<boolean>(false);
  const [savePhoneNumberToCustomer, setSavePhoneNumberToCustomer] = useState<boolean>(false);
  const [customer, setCustomer] = useState<ICustomer>();
  const [newBookedByEmail, setNewBookedByEmail] = useState<string>(null);
  const [saveBookedByEmailToBooking, setSaveBookedByEmailToBooking] = useState<boolean>(false);
  const [saveBookedByPhoneNumberToBooking, setSaveBookedByPhoneNumberToBooking] = useState<boolean>(false);
  // email must come from the customer object directly, as the `booking.email` is unreliable
  useEffect(() => {
    // When a Booking is of Type Walkin - There is no customer Id
    // this ends up with an error from backend 404 not found.
    // because here customerId = undefined.
    if (containerProps.booking.customerId) {
      CustomerLookupService.getById(containerProps.booking.customerId)
        .then((customer: ICustomer) => {
          setCustomer(customer);
          setNewEmail(customer?.email);
          PaymentValidationService.paymentEmail = customer?.email;
          setNewMobilePhone(customer?.phone);
          PaymentValidationService.paymentPhone = customer?.phone;
        });
    }
    if (props.enableBookedByOnDiary && containerProps.booking.isBookedBy) {
      setNewBookedByEmail(containerProps.booking.bookedBy?.email);
      setNewBookedByMobilePhone(containerProps.booking.bookedBy?.phone);
      PaymentValidationService.bookedByPaymentEmail = containerProps.booking.bookedBy?.email;
      PaymentValidationService.bookedByPaymentPhone = containerProps.booking.bookedBy?.phone;
    }
    return () => {};
  }, []);

  /**
   * Passes all base props from container to component
   */
  // @ts-ignore
  basePropsKeys.forEach(key => props[key] = containerProps[key]);

  const {isEmailValid, emailErrorMsg} = CustomerValidationService.emailValidationLink(newEmail);

  const {isBookedByEmailValid, emailErrorMsgBookedBy} = useMemo(() => {
    const {isEmailValid: isBookedByEmailValid, emailErrorMsg: emailErrorMsgBookedBy} = CustomerValidationService.emailValidationLink(newBookedByEmail);
    PaymentValidationService.bookedByPaymentEmail = newBookedByEmail;
    PaymentValidationService.isBookedByPaymentEmailValid = isBookedByEmailValid || !props.booking.bookedBy?.email;
    const _isBookedByEmailValid = isBookedByEmailValid || !props.booking.bookedBy?.email;
    if (!_isBookedByEmailValid) {
      setSaveBookedByEmailToBooking(false);
    }

    return {
      isBookedByEmailValid: _isBookedByEmailValid,
      emailErrorMsgBookedBy
    }
  }, [newBookedByEmail]);
  const res = PhoneNumberService.parsePhoneNumber(newMobilePhone);
  props.isPhoneValid = res?.isValid() || !customer?.phone;
  if (!res?.isValid()) {
    props.validPhoneMessage = 'Please enter a valid phone number';
  } else {
    props.validPhoneMessage = '';
  }
  const { isBookedByPhoneValid, validBookedByPhoneMessage } = useMemo(() => {
    const resBookedBy = PhoneNumberService.parsePhoneNumber(newBookedByMobilePhone);
    const _isBookedByPhoneValid = resBookedBy?.isValid() || !props.booking.bookedBy?.phone;
    props.bookedByMobilePhone = newBookedByMobilePhone;
    PaymentValidationService.bookedByPaymentPhone = newBookedByMobilePhone;
    PaymentValidationService.isBookedByPaymentPhoneValid = _isBookedByPhoneValid || !props.booking.bookedBy?.phone;
    if (!_isBookedByPhoneValid) {
      setSaveBookedByPhoneNumberToBooking(false);
    }
    return {
      isBookedByPhoneValid: _isBookedByPhoneValid,
      validBookedByPhoneMessage: resBookedBy?.isValid() ? '' : 'Please enter a valid phone number'
    }
  }, [newBookedByMobilePhone]);

  props.isBookedByPhoneValid = isBookedByPhoneValid
  props.validBookedByPhoneMessage = validBookedByPhoneMessage;
  props.isEmailValid = isEmailValid || !customer?.email;
  props.isBookedByEmailValid = isBookedByEmailValid || !props.booking.bookedBy?.email;
  props.validEmailMessage = emailErrorMsg;
  props.validBookedByEmailMessage = emailErrorMsgBookedBy;

  props.customer = customer;
  props.saveEmailToCustomer = saveEmailToCustomer;
  props.savePhoneNumberToCustomer = savePhoneNumberToCustomer;

  props.email = newEmail;
  props.mobilePhone = newMobilePhone;
  props.venueCountry = AdminService.getVenue().country;

  props.disableEmailSmsNotification = containerProps.booking.disableEmailSmsNotification || false;

  props.enableBookedByOnDiary = AdminService.getVenue().enableBookedByOnDiary;
  props.booking = containerProps.booking;
  props.bookedByEmail = newBookedByEmail;
  props.bookedByMobilePhone = newBookedByMobilePhone;
  props.saveBookedByEmailToBooking = saveBookedByEmailToBooking;
  props.saveBookedByPhoneNumberToBooking = saveBookedByPhoneNumberToBooking;
  props.isShorterContainerInEditBooking = containerProps.isShorterContainerInEditBooking;

  function setWatchedProps() {
    props.isStandbyPending = containerProps.booking.status.statusType === bookingStatusType.standbyPending;
    props.isStandbyBookingType = containerProps.booking.bookingType === BookingType.StandbyList;
    props.isBookingExpired = props.isStandbyPending ? DateUtilsService.checkIfStandbyConfirmationExpired(containerProps.booking.standByConfirmationExpiry) : false;
    props.smsEnabled = AdminService.getVenue().smsEnabled;
  }

  setWatchedProps();

  useEffect(() => {
    setWatchedProps();
    return () => {}
  }, [containerProps.booking.status, containerProps.booking.bookingType])

  /**
   * just exports props for unit tests
   */
  if (containerProps.isUnitTest) {
    return <div data-test-props={JSON.stringify(props)}></div>
  }
  const handleLinkPayment = async (booking: IBooking, email: string, saveEmail: boolean, url: string, bookedByNotification: boolean) => {
    try {
      const result = await CustomerNotificationService.sendLinkPayment(booking, email, saveEmail, url, bookedByNotification)
      if (result) {
        containerProps.handleSendLinkPaymentResponse(null);
      } else {
        containerProps.handleSendLinkPaymentResponse('Error: CustomerNotificationService.sendSmsLinkPayment $http');
      }
      return !!result;
    } catch (err: any) {
      console.warn('SendLinkPayment error', err);
      containerProps.handleSendLinkPaymentResponse(err.name);
      return false;
    }
  }
  const handleSmsLinkPayment = async (booking: IBooking, phone: string, savePhone: boolean, url: string, bookedByNotification: boolean) => {
    try {
      const result = await CustomerNotificationService.sendSmsLinkPayment(booking, phone, savePhone, url, bookedByNotification)
      if (result) {
        containerProps.handleSendLinkPaymentResponse(null);
      } else {
        containerProps.handleSendLinkPaymentResponse('Error: CustomerNotificationService.sendSmsLinkPayment $http');
      }
      return !!result;
    } catch (err: any) {
      console.warn('SendLinkPayment error', err);
      containerProps.handleSendLinkPaymentResponse(err.name);
      return false;
    }
  }
  const handleBookingUpdate = async (booking: IBooking) => {
    BookingUpdateService.updateInCouchOrPouchWhenOffline(booking, null).then((res) => {
      console.log('updateInCouchOrPouchWhenOffline res===>', res);
    }).catch((err) => {
      console.warn('updateInCouchOrPouchWhenOffline err===>', err);
    });
  }
  /**
   * Handlers used to modify app state (via a service) from within the module's container.
   * Similar to redux mapDispatchToProps.
   */
  const handlers: IHandlers = {

    handleEmailChanged: (value) => {
      setNewEmail(value);
      const {isEmailValid, emailErrorMsg} = CustomerValidationService.emailValidationLink(value);
      PaymentValidationService.paymentEmail = value;
      PaymentValidationService.isPaymentEmailValid = isEmailValid || !customer?.email;
      props.isEmailValid = isEmailValid || !customer?.email;
      props.validEmailMessage = emailErrorMsg;
      if (!props.isEmailValid) {
        setSaveEmailToCustomer(false);
      }
    },

    handleBookedByEmailChanged: (value) => {
      setNewBookedByEmail(value);
    },

    handleMobilePhoneChanged: (value) => {
      const res = PhoneNumberService.parsePhoneNumber(value);
      props.isPhoneValid = res?.isValid() || !customer?.phone;
      if (!res?.isValid()) {
        props.validPhoneMessage = 'Please enter a valid phone number';
      }
      props.mobilePhone = value;
      setNewMobilePhone(value);
      PaymentValidationService.paymentPhone = value;
      PaymentValidationService.isPaymentPhoneValid = props.isPhoneValid || !customer?.phone;
      if (!props.isPhoneValid) {
        setSavePhoneNumberToCustomer(false);
      }
    },
    handleBookedByMobilePhoneChanged: (value) => {
      setNewBookedByMobilePhone(value);
    },

    handleSaveEmailToCustomerChanged: (value) => {
      PaymentValidationService.isPaymentEmailUpdateChecked = !!value;
      setSaveEmailToCustomer(!!value);
    },
    handleSaveBookedByEmailToCustomerChanged: (value) => {
      PaymentValidationService.isBookedByPaymentEmailUpdateChecked = !!value;
      setSaveBookedByEmailToBooking(!!value);
    },
    handleSavePhoneNumberToCustomerChanged: (value) => {
      PaymentValidationService.isPaymentPhoneUpdateChecked = !!value;
      setSavePhoneNumberToCustomer(!!value);
    },
    handleSaveBookedByPhoneNumberToCustomerChanged: (value) => {
      PaymentValidationService.isBookedByPaymentPhoneUpdateChecked = !!value;
      setSaveBookedByPhoneNumberToBooking(!!value);
    },



    handleSendSmsLinkPayment: (isBookedBy: boolean) => {
      if (isBookedBy) {
        if (saveBookedByPhoneNumberToBooking) {
          props.booking.bookedBy.phone = newBookedByMobilePhone;
          BookingUpdateService.updateInCouchOrPouchWhenOffline(props.booking, null).then((res) => {
            console.log('handleSendSmsLinkPayment updateInCouchOrPouchWhenOffline success', res);
          }).catch(err => {
            console.warn('handleSendSmsLinkPayment error', err);
            containerProps.handleSendLinkPaymentResponse(err.name);
          })
        }
        return handleSmsLinkPayment(containerProps.booking, newBookedByMobilePhone, false, props.paymentUrl, true);
      }
      return handleSmsLinkPayment(containerProps.booking, newMobilePhone, savePhoneNumberToCustomer, props.paymentUrl, false);
    },

    handleSendLinkPayment: (isBookedBy: boolean) => {
      if (isBookedBy) {
        if (saveBookedByEmailToBooking) {
          props.booking.bookedBy.email = newBookedByEmail;
          BookingUpdateService.updateInCouchOrPouchWhenOffline(props.booking, null).then((res) => {
            console.log('updateInCouchOrPouchWhenOffline res===>', res);
          }).catch((err) => {
            console.warn('updateInCouchOrPouchWhenOffline err===>', err);
            containerProps.handleSendLinkPaymentResponse(err.name);
          });
        }
        return handleLinkPayment(containerProps.booking, newBookedByEmail, false, props.paymentUrl, true);
      }
      return handleLinkPayment(containerProps.booking, newEmail, saveEmailToCustomer, props.paymentUrl, false);
    },
    handleSaveButtonClick: () => {

        if (saveEmailToCustomer && props.isEmailValid) {
          customer.email = newEmail;
        }

        if (savePhoneNumberToCustomer && props.isPhoneValid) {
          customer.phone = newMobilePhone;
        }

        if (saveEmailToCustomer || savePhoneNumberToCustomer) {
          CustomerUpdateService.update(customer);
        }
        if (saveBookedByPhoneNumberToBooking || saveBookedByEmailToBooking) {
          if (props.booking.bookedBy.phone != newBookedByMobilePhone || props.booking.bookedBy.email != newBookedByEmail) {
            props.booking.bookedBy.phone = newBookedByMobilePhone;
            props.booking.bookedBy.email = newBookedByEmail;
            handleBookingUpdate(props.booking)
          }
        }
      props.handleSave();
    },
    handleCloseButtonClick:() => {
      props.handleSave();
    },
    handleOpenStandbyPayments: () => {
      const venue = AdminService.getVenue();

      return new Promise(resolve => {
        containerProps.handleWaitForSave()
          .then(({success, reason}) => {

            if (success) {
              LegacyStandbyListService.beginStandbyPayment(containerProps.booking._id, venue.id)
                .pipe(first())
                .subscribe(() => {
                  window.open(props.paymentUrl, '_blank');
                  resolve({success: true});
                }, error => {
                  console.warn(NS, 'beginStandbyPayment error', error);
                  resolve({success: false, reason: `Couldn't start Standby List timer`});
                });
            } else {
              console.warn(NS, 'handleOpenStandbyPayments', 'Failed to save booking before opening standby payment');
              resolve({success: false, reason});
            }
          });
      });
    }
  }

  return ReactUtilsService.containerWrap(
    <PaymentLinkInfo {...props} {...handlers}/>, classPrefix
  );
}

/**
 * Returns properties needed for rendering react2angular
 */
export const R2APaymentLinkInfo: [ComponentType<unknown>, never[]?, string[]?] = [
  hot(module)(ThemeWrap) as ComponentType<unknown>,
  [...containerPropsKeys] as never[]
];
